import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import type { GameKind, LobbySummary } from "../../../shared/protocol.js";
import { createLobby, listLobbies } from "../lib/api.js";
import { GAME_BLURBS, GAME_LABELS } from "../lib/gameKinds.js";
import { GameTilePicker } from "../components/GameTilePicker.js";
import { GameKindBadge } from "../components/GameKindBadge.js";
import { Select } from "../components/Select.js";
import { gameByKind } from "../games/registry.js";
import type { AppContext } from "../App.js";

export function HomePage() {
  const ctx = useOutletContext<AppContext>();
  const [lobbies, setLobbies] = useState<LobbySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [pickedKind, setPickedKind] = useState<GameKind | null>(null);
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const [error, setError] = useState<string | null>(null);
  // What kind is currently filtering the lobby list. `null` = show all.
  const [filterKind, setFilterKind] = useState<GameKind | null>(null);
  const navigate = useNavigate();

  async function refresh() {
    try {
      setLobbies(await listLobbies());
    } catch (e: any) {
      setError(e?.message ?? "Failed to fetch lobbies");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, []);

  // Derive live counts from the lobby list (only public lobbies come back
  // from /api/lobbies, which is exactly what we want to surface on a tile).
  const lobbyCounts = useMemo(() => {
    const counts: Partial<Record<GameKind, number>> = {};
    for (const l of lobbies) counts[l.gameKind] = (counts[l.gameKind] ?? 0) + 1;
    return counts;
  }, [lobbies]);

  const visibleLobbies = useMemo(
    () => (filterKind ? lobbies.filter((l) => l.gameKind === filterKind) : lobbies),
    [lobbies, filterKind]
  );

  function onPickKind(k: GameKind) {
    setPickedKind(k);
    // If the user hasn't customized the lobby name yet, suggest a default
    // tied to the game they just picked. They can still edit before create.
    if (newName.trim() === "") {
      setNewName(`${ctx.me?.displayName ?? "New"}'s ${GAME_LABELS[k]} table`);
    }
    // Also focus the lobby list on this game — most people browse and
    // create around the same kind, so reusing the click is a small win.
    setFilterKind(k);
  }

  function clearPick() {
    setPickedKind(null);
    setFilterKind(null);
    setNewName("");
  }

  async function onCreate() {
    setError(null);
    if (!pickedKind) {
      setError("Pick a game first");
      return;
    }
    const name = newName.trim() || `${ctx.me?.displayName ?? "New"}'s ${GAME_LABELS[pickedKind]} table`;
    try {
      const lobby = await createLobby({ name, gameKind: pickedKind, visibility });
      navigate(`/lobby/${lobby.id}`);
    } catch (e: any) {
      setError(e?.message ?? "Failed to create lobby");
    }
  }

  const pickedReg = pickedKind ? gameByKind(pickedKind) : null;

  return (
    <main className="container home-container">
      {error && <div className="toast">{error}</div>}

      {/* Hero + picker collapse once a game is chosen — keeps the create
         form and lobby list close to the top instead of pushing them down. */}
      {!pickedKind ? (
        <>
          <div className="home-hero">
            <h1>Pick a game.</h1>
            <p className="muted">
              Play with friends — no accounts needed. Pick a game, then create
              a table or join an open one below.
            </p>
          </div>

          <div className="card-block home-picker-block">
            <GameTilePicker
              value={pickedKind}
              onChange={onPickKind}
              lobbyCounts={lobbyCounts}
            />
          </div>
        </>
      ) : (
        <div className="card-block create-block">
          <div className="create-block-head">
            <button
              type="button"
              className="back-arrow-btn"
              onClick={clearPick}
              aria-label="Pick a different game"
              title="Pick a different game"
            >
              ←
            </button>
            <GameKindBadge kind={pickedKind} size={40} />
            <div>
              <h2 style={{ margin: 0 }}>Create a {GAME_LABELS[pickedKind]} table</h2>
              <p className="muted" style={{ margin: "4px 0 0" }}>
                {GAME_BLURBS[pickedKind]}
              </p>
            </div>
          </div>
          <div className="create-block-controls">
            <input
              placeholder={`${ctx.me?.displayName ?? "Your"}'s ${GAME_LABELS[pickedKind]} table`}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onCreate(); }}
              aria-label="Lobby name"
            />
            <Select<"public" | "private">
              value={visibility}
              onChange={setVisibility}
              options={[
                { value: "public", label: "Public", sublabel: "Anyone can find this lobby" },
                { value: "private", label: "Private", sublabel: "Link only" },
              ]}
              width={170}
            />
            <button className="primary create-btn" onClick={onCreate}>
              Create table
            </button>
          </div>
          {pickedReg?.seatBounds.min !== pickedReg?.seatBounds.max && (
            <p className="muted" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
              You'll pick the player count and any game options in the lobby.
            </p>
          )}
        </div>
      )}

      <div className="card-block">
        <div className="row between" style={{ alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>
            {filterKind ? `Open ${GAME_LABELS[filterKind]} lobbies` : "Open lobbies"}
          </h3>
          {filterKind && !pickedKind && (
            <button onClick={() => setFilterKind(null)} className="link-button">
              Show all
            </button>
          )}
        </div>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : visibleLobbies.length === 0 ? (
          <p className="muted">
            {filterKind
              ? `No open ${GAME_LABELS[filterKind]} lobbies yet. Be the first to create one!`
              : "No open lobbies yet. Pick a game above to create one."}
          </p>
        ) : (
          <div className="lobby-list">
            {visibleLobbies.map((l) => (
              <Link to={`/lobby/${l.id}`} key={l.id} className="lobby-row" style={{ color: "inherit" }}>
                <div className="meta">
                  <GameKindBadge kind={l.gameKind} size={28} />
                  <span className="name">{l.name}</span>
                  <span className="pill public">{GAME_LABELS[l.gameKind]}</span>
                  <span className="seats">
                    {l.seats.filter((s) => s !== null).length} / {l.seatCount} seats
                  </span>
                </div>
                <div className="muted">
                  {l.status === "in_game" ? "playing" : "waiting"}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
